import { playFromSpec } from '../../../effects/playFromSpec';
import { SOUND_LIBRARY, type SoundLibraryEntry } from '../../../effects/soundLibrary';
import type { SoundClip, SoundSpec, SoundVariant } from '../../../themes/types';
import { useVariantKeys } from './useVariantKeys';

interface SoundSectionProps {
  readonly value: SoundSpec;
  readonly onChange: (value: SoundSpec) => void;
}

export function SoundSection({ value, onChange }: SoundSectionProps) {
  const setClip = (kind: 'tap' | 'celebration', clip: SoundClip) =>
    onChange({ ...value, [kind]: clip });

  return (
    <fieldset className="theme-editor__section">
      <legend>Äänet</legend>
      <ClipEditor
        label="Napautus"
        value={value.tap}
        onChange={(c) => setClip('tap', c)}
        kind="tap"
      />
      <ClipEditor
        label="Juhla"
        value={value.celebration}
        onChange={(c) => setClip('celebration', c)}
        kind="celebration"
      />
    </fieldset>
  );
}

interface ClipProps {
  readonly label: string;
  readonly value: SoundClip;
  readonly onChange: (v: SoundClip) => void;
  readonly kind: 'tap' | 'celebration';
}

function ClipEditor({ label, value, onChange, kind }: ClipProps) {
  const keyFor = useVariantKeys<SoundVariant>();
  const preview = (entry: SoundLibraryEntry) => {
    const spec: SoundSpec = {
      tap: { variants: [entry.variant], pickMode: 'sequential' },
      celebration: { variants: [entry.variant], pickMode: 'sequential' },
    };
    playFromSpec(spec, kind);
  };

  const addFromLibrary = (entry: SoundLibraryEntry) =>
    onChange({ ...value, variants: [...value.variants, entry.variant] });
  const remove = (idx: number) =>
    onChange({ ...value, variants: value.variants.filter((_, i) => i !== idx) });

  return (
    <div className="theme-editor__sound-clip">
      <h3>{label}</h3>
      <ul>
        {value.variants.map((v, i) => (
          <li key={keyFor(v)}>
            {describeVariant(v)}
            {value.variants.length > 1 ? (
              <button type="button" onClick={() => remove(i)} aria-label="Poista">
                ✕
              </button>
            ) : null}
          </li>
        ))}
      </ul>
      <div className="theme-editor__sound-library">
        {SOUND_LIBRARY.map((entry) => (
          <div key={entry.key}>
            <button type="button" onClick={() => addFromLibrary(entry)}>
              + {entry.name}
            </button>
            <button type="button" onClick={() => preview(entry)} aria-label={entry.previewLabel}>
              Kuuntele
            </button>
          </div>
        ))}
      </div>
      {value.variants.length > 1 ? (
        <label>
          <span>Valintatapa</span>
          <select
            value={value.pickMode}
            onChange={(e) =>
              onChange({ ...value, pickMode: e.target.value as 'sequential' | 'random' })
            }
          >
            <option value="sequential">Vuoro</option>
            <option value="random">Arpa</option>
          </select>
        </label>
      ) : null}
    </div>
  );
}

function describeVariant(v: SoundClip['variants'][number]): string {
  switch (v.kind) {
    case 'chord':
      return v.notes.length === 0 ? 'Hiljainen' : `Sointu: ${v.notes.join(', ')}`;
    case 'note':
      return `Nuotti: ${v.note}`;
    case 'sample': {
      const basename = v.url.split('/').pop() ?? v.url;
      return `Näyte: ${basename}`;
    }
  }
}
